// ── Navigation Module ─────────────────────────────────────────────
// Manages the left sidebar stepper UI and right panel visibility.

import { getState, setState, subscribe } from './state.js';
import { STEP_LABELS, STEP_PANELS } from './constants.js';
import { getMaxNavigableStep, isStepCompleted, syncNavigationBounds } from './navigation-bounds.js';

// ── Step State Determination ────────────────────────────────────

/**
 * Returns the visual state for a given step number.
 * @param {number} step
 * @returns {'active' | 'completed' | 'locked' | 'pending'}
 */
function getStepState(step) {
  const current = getState('currentStep');
  const maxNav = getMaxNavigableStep();

  if (step === current) return 'active';
  if (step === 4 && current >= 4) return 'active';
  if (step > maxNav) return 'pending';
  if (isStepCompleted(step)) return 'completed';
  return 'available';
}

// ── Sidebar UI Update ──────────────────────────────────────────

function updateSidebarStepper() {
  const sidebar = document.querySelector('.sidebar .stepper');
  if (!sidebar) return;

  const current = getState('currentStep');
  const highest = getState('highestStep');
  const totalSteps = Object.keys(STEP_LABELS).length;

  sidebar.querySelectorAll('.step-item').forEach((item) => {
    const step = parseInt(item.dataset.step, 10);
    const state = getStepState(step);

    item.classList.remove('active', 'completed', 'disabled', 'locked', 'available');
    if (state === 'active') item.classList.add('active');
    if (state === 'completed') item.classList.add('completed');
    if (state === 'available') item.classList.add('available');
    if (state === 'locked') item.classList.add('locked');
    if (state === 'pending') item.classList.add('disabled');
    const isDisabled = state === 'locked' || state === 'pending';
    item.setAttribute('aria-disabled', String(isDisabled));
    item.tabIndex = isDisabled ? -1 : 0;
    if (step === current) item.setAttribute('aria-current', 'step');
    else item.removeAttribute('aria-current');
  });

  // Update progress bar (driven by highestStep, not currentStep)
  const progressCount = document.getElementById('progress-count');
  const progressFill = document.getElementById('progress-fill');
  const progressDone = Math.min(Math.max(highest - 1, 0), totalSteps - 1);
  if (progressCount) progressCount.textContent = `${progressDone}/${totalSteps}`;
  if (progressFill) {
    progressFill.max = totalSteps;
    progressFill.value = progressDone;
  }

  // Also update sidebar badge text for locked/completed state
  sidebar.querySelectorAll('.step-item').forEach((item) => {
    const step = parseInt(item.dataset.step, 10);
    const state = getStepState(step);
    const badge = item.querySelector('.step-badge');
    if (!badge) return;
    if (state === 'completed') badge.textContent = '已完成';
    else if (state === 'available') badge.textContent = '可查看';
    else if (state === 'pending') badge.textContent = '待解锁';
    else if (state === 'active') badge.textContent = '进行中';
  });
}

// ── Right Panel Visibility ───────────────────────────────────────

function showStepPanel(step) {
  const panelId = STEP_PANELS[step];
  if (!panelId) return;

  // Hide all panels
  Object.values(STEP_PANELS).forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.hidden = true;
  });

  // Show the target panel
  const panel = document.getElementById(panelId);
  if (panel) {
    panel.hidden = false;
    panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

// ── Event Handling ───────────────────────────────────────────────

function handleStepItemClick(e) {
  const item = e.target.closest('.step-item');
  if (!item) return;
  if (item.classList.contains('disabled')) return;

  const step = parseInt(item.dataset.step, 10);
  if (isNaN(step)) return;

  const maxNav = getMaxNavigableStep();
  if (step > maxNav) return;

  setState('currentStep', step);
}

function handleStepItemKeydown(event) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  handleStepItemClick(event);
}

/** 程序化跳转步骤（仅生图工坊等） */
export function navigateToStep(step) {
  setState('currentStep', step);
}

/** 仅生图工坊：只切换 step-image 面板，不挂载完整 7 步导航与 bounds 订阅 */
export function initAssetsNavigation() {
  subscribe('state:currentStepChanged', ({ curr }) => {
    const setup = document.getElementById('assets-setup-panel');
    if (curr === 7) {
      if (setup) setup.hidden = true;
      showStepPanel(7);
    }
  });
}

// ── Public Init ──────────────────────────────────────────────────

export function initNavigation() {
  if (getState('workflowMode') === 'assets') {
    initAssetsNavigation();
    return;
  }
  const sidebar = document.querySelector('.sidebar .stepper');
  if (!sidebar) return;

  // Bind click events on step items
  sidebar.querySelectorAll('.step-item').forEach((item) => {
    item.addEventListener('click', handleStepItemClick);
    item.addEventListener('keydown', handleStepItemKeydown);
  });

  // Subscribe to currentStep changes → update sidebar + panel
  subscribe('state:currentStepChanged', ({ curr }) => {
    updateSidebarStepper();
    showStepPanel(curr);
  });

  subscribe('state:highestStepChanged', () => updateSidebarStepper());
  subscribe('state:stepReadyChanged', () => {
    syncNavigationBounds();
    updateSidebarStepper();
  });
  subscribe('state:complianceResultChanged', () => {
    syncNavigationBounds();
    updateSidebarStepper();
  });
  subscribe('state:scoringReportChanged', () => {
    syncNavigationBounds();
    updateSidebarStepper();
  });
  subscribe('state:revisionSuggestionsChanged', () => {
    syncNavigationBounds();
    updateSidebarStepper();
  });
  subscribe('state:listingDraftTitleChanged', () => {
    syncNavigationBounds();
    updateSidebarStepper();
  });
  subscribe('state:keywordLibraryChanged', () => {
    syncNavigationBounds();
    updateSidebarStepper();
  });

  syncNavigationBounds();
  updateSidebarStepper();
  showStepPanel(getState('currentStep'));
}
